import { useInfiniteQuery } from '@tanstack/react-query';
import { ScrollText } from 'lucide-react';
import { useState } from 'react';
import { api } from '@/api/cliente';
import { EncabezadoPagina, ModuleLayout } from '@/components/layout/ModuleLayout';
import { SECCIONES_POR_MODULO } from '@/components/layout/secciones';
import { Button } from '@/components/ui/button';
import { Esqueleto, Tabla, Tarjeta, Td, Th, Tr, Vacio } from '@/components/ui/data';
import { Select } from '@/components/ui/form';
import { fechaHora } from '@/lib/utils';

interface EntradaAuditoria {
  id: string;
  accion: string;
  entidad: string;
  entidadId: string | null;
  actorNombre: string | null;
  fecha: string;
  antes: unknown;
  despues: unknown;
}

const ENTIDADES = ['', 'usuario', 'configuracion', 'sucursal', 'cita', 'venta', 'turno_caja', 'producto', 'pago_semanal', 'aprobacion'];

export function AuditoriaPage() {
  const [entidad, setEntidad] = useState('');
  const consulta = useInfiniteQuery({
    queryKey: ['auditoria', entidad],
    queryFn: ({ pageParam }) =>
      api<{ items: EntradaAuditoria[]; siguienteCursor: string | null }>('/auditoria', {
        query: { entidad: entidad || undefined, cursor: pageParam ?? undefined, limite: 50 },
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (ultima) => ultima.siguienteCursor,
  });
  const items = consulta.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <ModuleLayout titulo="Configuración" secciones={SECCIONES_POR_MODULO.configuracion}>
      <EncabezadoPagina
        titulo="Bitácora"
        descripcion="Registro de quién hizo qué y cuándo en operaciones sensibles."
        acciones={
          <Select value={entidad} onChange={(e) => setEntidad(e.target.value)} aria-label="Filtrar por tipo de registro" className="w-48">
            {ENTIDADES.map((e) => (
              <option key={e} value={e}>
                {e ? e.replace('_', ' ') : 'Todos los registros'}
              </option>
            ))}
          </Select>
        }
      />
      <Tarjeta>
        {consulta.isLoading ? (
          <div className="p-4">
            <Esqueleto className="h-40" />
          </div>
        ) : items.length === 0 ? (
          <Vacio icono={<ScrollText />} titulo="Sin registros" />
        ) : (
          <Tabla>
            <thead>
              <tr>
                <Th>Fecha</Th>
                <Th>Usuario</Th>
                <Th>Acción</Th>
                <Th>Registro</Th>
                <Th>Detalle</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <Tr key={i.id}>
                  <Td className="cifras whitespace-nowrap text-foreground-muted">{fechaHora(i.fecha)}</Td>
                  <Td>{i.actorNombre ?? 'Sistema'}</Td>
                  <Td className="font-medium">{i.accion}</Td>
                  <Td className="text-foreground-muted">
                    {i.entidad}
                    {i.entidadId ? ` · ${i.entidadId.slice(0, 8)}` : ''}
                  </Td>
                  <Td className="max-w-md">
                    {i.despues != null && (
                      <details>
                        <summary className="cursor-pointer text-[12.5px] text-accent">Ver cambios</summary>
                        <pre className="mt-1 max-h-48 overflow-auto rounded bg-muted p-2 text-[11.5px]">
                          {JSON.stringify({ antes: i.antes, despues: i.despues }, null, 2)}
                        </pre>
                      </details>
                    )}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Tabla>
        )}
        {consulta.hasNextPage && (
          <div className="flex justify-center border-t border-border p-3">
            <Button variante="secundario" onClick={() => void consulta.fetchNextPage()} cargando={consulta.isFetchingNextPage}>
              Cargar más
            </Button>
          </div>
        )}
      </Tarjeta>
    </ModuleLayout>
  );
}
